import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { GET_BALANCE_WATCH_ALERT_RULES, UPDATE_BALANCE_WATCH_ALERT_RULES } from './graphql';

type AlertRules = {
  windowSeconds: number;
  sameRewardMaxGrants: number;
  sameRewardMaxGrantsNoDetail: number;
  resourceMaxGrants: number;
  maxAlertsPerWindow: number;
  cooldownSeconds: number;
  mutedUserIds: string[];
};

type NumericField = Exclude<keyof AlertRules, 'mutedUserIds'>;

type Draft = {
  numbers: Record<NumericField, string>;
  mutedUserIdsText: string;
};

const ordinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
};

const timesHelp = (what: string) => (value: number) =>
  Number.isInteger(value) && value >= 1
    ? value === 1
      ? `Alerts on every ${what} payout.`
      : `Alerts when ${what} is paid the ${ordinal(value)} time in the watch period.`
    : 'Enter a whole number of 1 or more.';

const FIELDS: Array<{
  key: NumericField;
  label: string;
  help: string | ((value: number) => string);
  min: number;
  max: number;
}> = [
  { key: 'windowSeconds', label: 'Watch period (seconds)', help: 'Payouts are counted within this time.', min: 5, max: 86400 },
  {
    key: 'sameRewardMaxGrants',
    label: 'Same reward paid X times',
    help: timesHelp('the same reward'),
    min: 1,
    max: 1000,
  },
  {
    key: 'sameRewardMaxGrantsNoDetail',
    label: 'Same feature paid X times',
    help: timesHelp('a feature that does not name its reward'),
    min: 1,
    max: 1000,
  },
  { key: 'resourceMaxGrants', label: 'Any payout X times', help: timesHelp('any reward'), min: 1, max: 10000 },
  { key: 'maxAlertsPerWindow', label: 'Max alerts per watch period', help: 'Including the first one.', min: 1, max: 100 },
  { key: 'cooldownSeconds', label: 'Quiet time (seconds)', help: 'No alerts after the watch period that raised the first one.', min: 1, max: 604800 },
];

const SECTIONS: Array<{ title: string; hint: string; keys: NumericField[] }> = [
  { title: 'Watch period', hint: 'Used by both rules below.', keys: ['windowSeconds'] },
  {
    title: 'Rule 1 - same reward again',
    hint: 'Catches one reward being claimed over and over.',
    keys: ['sameRewardMaxGrants', 'sameRewardMaxGrantsNoDetail'],
  },
  {
    title: 'Rule 2 - too many payouts overall',
    hint: 'Safety net: many different rewards, none repeated.',
    keys: ['resourceMaxGrants'],
  },
  {
    title: 'Alerts',
    hint: 'Per reward: max messages within one watch period, then how long to stay quiet.',
    keys: ['maxAlertsPerWindow', 'cooldownSeconds'],
  },
];

const parseUserIds = (text: string): string[] =>
  Array.from(new Set(text.split(/[\s,;]+/).map((id) => id.trim()).filter(Boolean)));

const toDraft = (rules: AlertRules): Draft => ({
  numbers: FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: String(rules[key]) }), {} as Record<NumericField, string>),
  mutedUserIdsText: (rules.mutedUserIds ?? []).join('\n'),
});

const parseDraft = (draft: Draft): AlertRules => {
  const numbers = {} as Record<NumericField, number>;
  FIELDS.forEach(({ key, label, min, max }) => {
    const value = Number(draft.numbers[key]);
    if (!Number.isInteger(value) || value < min || value > max) {
      throw new Error(`${label} must be a whole number between ${min} and ${max}.`);
    }
    numbers[key] = value;
  });
  return { ...numbers, mutedUserIds: parseUserIds(draft.mutedUserIdsText) };
};

const formatDuration = (seconds: number) => {
  if (seconds % 3600 === 0) {
    return `${seconds / 3600} h`;
  }
  if (seconds % 60 === 0) {
    return `${seconds / 60} min`;
  }
  return `${seconds} s`;
};

const isSameRules = (saved: AlertRules, draft: Draft) => {
  try {
    const parsed = parseDraft(draft);
    return (
      FIELDS.every(({ key }) => saved[key] === parsed[key]) &&
      JSON.stringify([...(saved.mutedUserIds ?? [])].sort()) === JSON.stringify([...parsed.mutedUserIds].sort())
    );
  } catch {
    return false;
  }
};

export default function AlertRulesCard() {
  const { data, loading, error, refetch } = useQuery(GET_BALANCE_WATCH_ALERT_RULES, { fetchPolicy: 'network-only' });
  const [updateRules, { loading: saving }] = useMutation(UPDATE_BALANCE_WATCH_ALERT_RULES);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  const saved: AlertRules | undefined = data?.liveOps?.balanceWatchAlertRulesConfig;

  useEffect(() => {
    if (saved && !draft) {
      setDraft(toDraft(saved));
    }
  }, [saved, draft]);

  const preview = (() => {
    if (!draft) {
      return null;
    }
    try {
      const rules = parseDraft(draft);
      const muted = rules.mutedUserIds.length ? ` ${rules.mutedUserIds.length} muted.` : '';
      return (
        `Alert when the same reward pays ${rules.sameRewardMaxGrants} times within ${formatDuration(rules.windowSeconds)}` +
        ` (or any ${rules.resourceMaxGrants} payouts). Up to ${rules.maxAlertsPerWindow} alerts in that period,` +
        ` then quiet for ${formatDuration(rules.cooldownSeconds)}, per reward.${muted}`
      );
    } catch (e) {
      return (e as Error).message;
    }
  })();

  const save = async () => {
    if (!draft) {
      return;
    }
    setFeedback(null);
    try {
      const input = parseDraft(draft);
      const { data: result } = await updateRules({ variables: { input } });
      const next: AlertRules | undefined = result?.updateBalanceWatchAlertRulesConfig;
      if (next) {
        setDraft(toDraft(next));
      }
      await refetch();
      setFeedback({ type: 'success', message: 'Saved.' });
    } catch (e) {
      setFeedback({ type: 'danger', message: (e as Error).message });
    }
  };

  const isDirty = !!saved && !!draft && !isSameRules(saved, draft);

  return (
    <Card className="m-4">
      <Card.Header>Balance Watch - alert rules</Card.Header>
      <Card.Body>
        <Alert variant="info" className="small mb-3">
          When to post an alert to Mattermost. Changes apply right away.
        </Alert>
        {loading && !draft && <Spinner animation="border" size="sm" />}
        {error && <Alert variant="danger">{error.message}</Alert>}
        {draft && (
          <>
            {SECTIONS.map(({ title, hint, keys }) => (
              <div key={title}>
                <div className="font-weight-bold small">{title}</div>
                <div className="text-muted small mb-2">{hint}</div>
                <Row>
                  {FIELDS.filter(({ key }) => keys.includes(key)).map(({ key, label, help }) => (
                    <Col md={6} lg={4} key={key} className="mb-3">
                      <Form.Label className="small mb-1">{label}</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        style={{ width: 110 }}
                        value={draft.numbers[key]}
                        onChange={(e) => setDraft({ ...draft, numbers: { ...draft.numbers, [key]: e.target.value } })}
                      />
                      <Form.Text className="text-muted">
                        {typeof help === 'function' ? help(Number(draft.numbers[key])) : help}
                      </Form.Text>
                    </Col>
                  ))}
                </Row>
                <hr className="mt-0" />
              </div>
            ))}
            <div className="font-weight-bold small">Muted players</div>
            <div className="text-muted small mb-2">Known cheaters. Still tracked, never posted.</div>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              className="mb-3"
              style={{ maxWidth: 520 }}
              placeholder="User IDs, one per line"
              value={draft.mutedUserIdsText}
              onChange={(e) => setDraft({ ...draft, mutedUserIdsText: e.target.value })}
            />
            <Alert variant={preview && preview.startsWith('Alert when') ? 'secondary' : 'warning'} className="small">
              {preview}
            </Alert>
            {feedback && <Alert variant={feedback.type}>{feedback.message}</Alert>}
            <Button size="sm" variant="primary" onClick={save} disabled={saving || !isDirty}>
              {saving ? 'Saving…' : 'Save rules'}
            </Button>{' '}
            <Button size="sm" variant="outline-secondary" onClick={() => saved && setDraft(toDraft(saved))} disabled={!isDirty}>
              Reset
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
